//Hooks
import { writable } from 'svelte/store';
//Stores
import { user_settings } from 'src/stores/account/user';
//Helpers
import { weatherTypes } from 'src/components/Weather/helpers/weather_uttilities';
import { mobile } from 'src/services/mobile-rework';
//Services
import { postReq } from '../services/http';
//Utilities
import { mathEpsilon } from './utilities';
//Types
import type {
	ForecastData,
	RegWeatherServiceResponse,
	WeatherData,
	WeatherServiceResponse,
	WeatherStationSaveData,
} from '../types/requests/services/weather';
import type { CloudApiHttpResponse } from 'src/services/utils/interfaces/cloud-api-reponse';

export const mapbox_map_config = {
	apiKey: 'pk.eyJ1IjoiYS1hbGx0ZXJjbyIsImEiOiJjbGc0d28xNjcwMHduM2VvNmNicmhqeXkwIn0.I9nhjUTi7fyQ6QnVJ7Q4xg',
	style: 'mapbox://styles/a-allterco/clg4x54d8008501mykkfls7x3',
	minZoom: 1.5,
	maxZoom: 20,
};

export const checkGeolocationPosition = () => {
	if (mobile.isMobile) {
		return new Promise((resolve, reject) => {
			const removeEventListeners = () => {
				mobile.removeEventListener('device_geolocation', onDeviceGeolocation);
				mobile.removeEventListener('pause_app', onDeviceLocPermission);
			};
			const onDeviceGeolocation = (e: CustomEvent) => {
				removeEventListeners();
				const lat: number = e.detail[0],
					lng = e.detail[1];
				if (isNaN(lat) || isNaN(lng)) {
					reject({
						message: 'Please enable location services on your device and try again.',
					});
				} else {
					resolve({ lat: Number(lat), lng: Number(lng) });
				}
			};
			const onDeviceLocPermission = () => {
				removeEventListeners();
				reject();
			};
			mobile.addEventListener('device_geolocation', onDeviceGeolocation);
			mobile.addEventListener('pause_app', onDeviceLocPermission);
			mobile.exec('device_geolocation');
		});
	}
	return new Promise((resolve, reject) => {
		navigator.geolocation.getCurrentPosition(
			(pos) => {
				const data = {
					lat: Number(pos.coords.latitude),
					lng: Number(pos.coords.longitude),
				};
				resolve(data);
			},
			(error) => reject(error),
			{ enableHighAccuracy: true, timeout: 7000, maximumAge: 0 },
		);
	});
};

export const validateGeolocation = (lat: number, lng: number): Boolean => {
	return lat >= -90 && lat <= 90 && lng >= -180 && lng <= 180;
};

export const getListWeatherStations = async (data: {
	lat: number;
	lon: number;
}): Promise<WeatherServiceResponse> => {
	try {
		const req = await fetch(`https://weather.shelly.cloud/near?lat=${data.lat}&lon=${data.lon}`);
		return await req.json();
	} catch (err) {
		console.log(err);
		return null;
	}
};

export const addWeatherStation = async (
	data: WeatherStationSaveData,
): Promise<RegWeatherServiceResponse> => {
	try {
		const req = await fetch(`https://weather.shelly.cloud/reg?l=${data.weatherID}`),
			res: RegWeatherServiceResponse = await req.json();
		if (res.isok) {
			if (res.data.old) {
				const saveStation = await saveWeatherStation(data);
				if (saveStation?.isok) getWeatherStationData(res.data.l);
			}
		}
		return res;
	} catch (err) {
		console.warn(err);
		return null;
	}
};

export const saveWeatherStation = async (weatherData: WeatherStationSaveData) => {
	const res = await postReq('/user/set_weather_station', {
		data: JSON.stringify(weatherData),
		name: 'default',
		l: weatherData.stationID,
	});
	if (res?.isok) {
		user_settings.update((settings) => {
			settings.weather_station = {
				l: weatherData.stationID,
				data: weatherData,
			};
			return settings;
		});
	}
	return res;
};

const createWeatherStore = () => {
	const { subscribe, set, update } = writable();
	return {
		set,
		subscribe,
		update: (data: ForecastData) => {
			update(() => data);
		},
		reset: () => set(null),
	};
};

let nextWeatherReqTimeID: NodeJS.Timeout;
let nextWeatherReqIncTime = 1;

export const getWeatherStationData = async (stationID: number, init = false): Promise<void> => {
	clearTimeout(nextWeatherReqTimeID);
	try {
		const req = await fetch(`https://weather.shelly.cloud/get?l=${stationID}`),
			res = await req.json();
		// console.log('%cres', 'color: orange', res)
		// vto valid to - timestamp for next planed cache refresh
		// tst timestamp for the conditions in the block
		// cond "current" conditions
		// f forecast
		if (res?.isok && res?.data) {
			const weather: WeatherData = {
				...parseForecastData(res.data.cond.c),
				stationID: res.data.cond.i,
				timeCache: Date.now(),
				nextPlanedCache: new Date(res.data.vto).getTime() + 60000,
				forecast: res.data.cond.f.map((i) => parseForecastData(i)),
			};
			// console.log('%cweather', 'color: orange', weather)
			weather_store.update(weather);
			nextWeatherReqIncTime = 1;
		} else if (res?.isok === false && res?.data?.l) {
			if (init) {
				try {
					await fetch(`https://weather.shelly.cloud/reg?l=${res.data.l}`);
				} catch (err) {
					console.warn(err);
					return null;
				}
			}
			console.warn('Get weather conditions to request failed. Try again after 5s');
			nextWeatherReqTimeID = setTimeout(
				() => getWeatherStationData(res.data.l),
				5000 * nextWeatherReqIncTime++,
			);
		}
	} catch (err) {
		console.warn(err);
	}
};

export const weather_store: any = createWeatherStore();
weather_store.subscribe((data: WeatherData) => {
	if (data) {
		clearTimeout(nextWeatherReqTimeID);
		const nextWeatherRequestTime = Math.max(5 * 60000, data.nextPlanedCache - data.timeCache);
		nextWeatherReqTimeID = setTimeout(
			() => getWeatherStationData(data.stationID),
			nextWeatherRequestTime,
		);
	}
});

const parseForecastData = (data: any): ForecastData => {
	// t temp,
	// h humidity,
	// p pressure,
	// w wind speed
	// dd direction
	// d direction (degrees)
	// fl temperature "feels like"
	// c clouds %
	// cc clouds string
	// cci clouds integer code
	// pop possibility for precipitation 0.0 to 1.0
	// tc thunderstorm code
	// dc drizzle code
	// rc rain code
	// sc snow code
	// ac atmosphere code
	const time = new Date(data.tst * 1000),
		weatherType = interpretWeatherData(data, time);
	return {
		...weatherType,
		time,
		temperature: `${mathEpsilon(data.t, 1)}°`,
		humidity: `${mathEpsilon(data.h, 1)}%`,
		wind: `${mathEpsilon(data.w, 1)} m/s ${data.dd}`,
		clouds: `${data.c}%`,
		pop: `${Math.round(data.pop * 100)}%`,
		// feelsLike: `${data.fl}°C`,
		// pressure: `${data.p}hPa`,
		// data
	};
};

const interpretWeatherData = (data: any, time: Date): any => {
	const hour = time.getHours();
	let clearWeatherType = weatherTypes.clear;
	for (const typeName in weatherTypes) {
		const weatherType = weatherTypes[typeName];

		if (Number(data[weatherType.code]) == weatherType.consition) {
			clearWeatherType = weatherType;
			break;
		}
	}
	if (/^(sunny|clear)$/i.test(clearWeatherType.type) && (hour <= 6 || hour >= 21)) {
		return { ...weatherTypes.clear, icon: 'fas fa-moon-stars' };
	}

	return clearWeatherType;
};

export const getTimeZoneName = async (lat: number, lng: number): Promise<string> => {
	const res: CloudApiHttpResponse = await postReq(`/device/get_timezone?location=${lat},${lng}`, {});
	if (!res?.isok) {
		console.warn('Timezone fetch error:', res);
		return null;
	}
	return res?.data?.timeZoneId || null;
};

export const getElevation = async (lat: number, lng: number): Promise<number> => {
	const res: CloudApiHttpResponse = await postReq(`/device/get_elevation?locations=${lat},${lng}`, {});
	if (!res?.isok) {
		console.warn('Elevation fetch error:', res);
		return null;
	}
	return res?.data?.[0]?.['elevation'] ?? null;
};
